import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { ChevronLeftIcon, ChevronRightIcon, FunnelIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';

const CitedMarks = () => {
  const navigate = useNavigate();
  const [citedMarksData, setCitedMarksData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Set default to show all records (no date filter)
  useEffect(() => {
    fetchCitedMarksData();
  }, []);

  const formatDateForAPI = (dateString) => {
    if (!dateString) return '';
    // Convert YYYY-MM-DD (from date picker) to d/m/Y format for API
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // d/m/Y format
  };

  const getLast7Days = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    // Format for date picker (YYYY-MM-DD)
    const formatDateForPicker = (date) => {
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    };
    
    setFromDate(formatDateForPicker(sevenDaysAgo));
    setToDate(formatDateForPicker(today));
  };

  const getLast30Days = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    // Format for date picker (YYYY-MM-DD)
    const formatDateForPicker = (date) => {
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    };
    
    setFromDate(formatDateForPicker(thirtyDaysAgo));
    setToDate(formatDateForPicker(today));
  };

  const fetchCitedMarksData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('📡 Fetching cited marks data...');
      
      const params = {
        url: `https://agent3.tmpilot.com/api/citedMarks?token={token}`
      };

      // Add date filters if provided
      if (fromDate) {
        params.url += `&fromdate=${formatDateForAPI(fromDate)}`;
      }
      if (toDate) {
        params.url += `&todate=${formatDateForAPI(toDate)}`;
      }

      const response = await api.post('/api-data/get-data', params);

      if (response.data.success) {
        console.log('✅ Cited marks data received:', response.data.data);
        const data = response.data.data;
        
        if (data.data && Array.isArray(data.data)) {
          // Sort by citedDate (latest to oldest)
          const sortedData = data.data.sort((a, b) => {
            const dateA = new Date(a.citedDate.split('/').reverse().join('-'));
            const dateB = new Date(b.citedDate.split('/').reverse().join('-'));
            return dateB - dateA;
          });
          
          setCitedMarksData(sortedData);
          toast.success(`Loaded ${sortedData.length} cited marks records`);
        } else {
          throw new Error('Invalid data format received from API');
        }
      } else {
        throw new Error(response.data.message || 'Failed to load cited marks data');
      }
    } catch (error) {
      console.error('Error fetching cited marks:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load cited marks data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchCitedMarksData();
  };

  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
    fetchCitedMarksData();
  };

  const handleAppNoClick = (appno) => {
    navigate(`/api-data/trademark-portfolio/${appno}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      // Handle d/m/Y format
      if (dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          const date = new Date(parts[2], parts[1] - 1, parts[0]);
          return date.toLocaleDateString('en-GB');
        }
      }
      // Handle other date formats
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch (error) {
      return dateString;
    }
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      // Prepare data for export
      const exportData = citedMarksData.map(item => ({
        'Cited App No': item.appno,
        'Your Application No': item.trademark?.appno || '',
        'Your Company': item.buisnessName || '',
        'Your TM': item.trademark?.tmAppliedFor || '',
        'Conflict TM': item.citedTmName || '',
        'Cited Company': item.citedCompanyName || '',
        'Date of Citation': item.citedDate || '',
        'Class': item.citedClass || '',
        'Type': item.type || '',
        'Jurisdiction': item.Jurisdiction || '',
        'Exam Report URL': item.exam_report_url || ''
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const colWidths = [
        { wch: 25 }, // Your TM
        { wch: 25 }, // Conflict TM
        { wch: 15 }, // Date of Citation
        { wch: 10 }, // Class
        { wch: 10 }, // Type
        { wch: 10 }, // Jurisdiction
        { wch: 15 }  // Exam Report
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Cited Marks');

      // Generate filename with current date
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      const filename = `cited_marks_${dateStr}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      
      toast.success('Excel file exported successfully!');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export Excel file');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Cited Marks</h1>
              <p className="mt-1 text-sm text-gray-500">
                Track cited marks and conflicts in trademark applications
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
              </button>
              <button
                onClick={exportToExcel}
                disabled={exporting || citedMarksData.length === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export Excel'}
              </button>
              <button
                onClick={() => fetchCitedMarksData()}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Filter Options</h3>
            <form onSubmit={handleFilterSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                
                <div className="flex items-end space-x-2">
                  <button
                    type="button"
                    onClick={getLast7Days}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Last 7 Days
                  </button>
                  <button
                    type="button"
                    onClick={getLast30Days}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Last 30 Days
                  </button>
                </div>
                
                <div className="flex items-end space-x-2">
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Cited Marks Records ({citedMarksData.length} total)
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Sorted by latest citation date first
            </p>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-white bg-blue-500 hover:bg-blue-400 transition ease-in-out duration-150 cursor-not-allowed">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading cited marks data...
              </div>
            </div>
          ) : citedMarksData.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">No cited marks found for the selected criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {/* <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cited App No
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Your Application No
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Your Company
                    </th> */}
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Your TM
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Conflict TM
                    </th>
                    {/* <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cited Company
                    </th> */}
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date of Citation
                    </th>
                    {/* <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Class
                    </th> */}
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Jurisdiction
                    </th>
                    {/* <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Exam Report
                    </th> */}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {citedMarksData.map((item, index) => (
                    <tr key={item.id || index} className="hover:bg-gray-50">
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      <button
                          onClick={() => handleAppNoClick(item.appno)}
                          className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                          disabled={!item.appno}
                        >
                          {item.appno}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        <button
                          onClick={() => handleAppNoClick(item.trademark?.appno)}
                          className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                          disabled={!item.trademark?.appno}
                        >
                          {item.trademark?.appno || '-'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.buisnessName || '-'}
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.trademark?.tmAppliedFor || '-'}
                        <br></br>
                        <span className="text-gray-500 text-xs">
                        <button
                          onClick={() => handleAppNoClick(item.trademark?.appno)}
                          className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                        >
                          {item.trademark?.appno || '-'}
                        </button> - {item.citedClass || '-'} - {item.buisnessName || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        
                        {item.citedTmName || '-'}
                        <span className="text-gray-500 text-xs">
                        <br></br>
                        <button
                          onClick={() => handleAppNoClick(item.appno)}
                          className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                        >
                          {item.appno}
                        </button> - 
                        {item.citedCompanyName || '-'}
                        </span>
                          
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                        {item.citedCompanyName || '-'}
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(item.citedDate)}<br></br>
                        {item.exam_report_url ? (
                          <a
                            href={item.exam_report_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            View Report
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.citedClass || '-'}
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.type || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.Jurisdiction || '-'}
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.exam_report_url ? (
                          <a
                            href={item.exam_report_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            View Report
                          </a>
                        ) : (
                          '-'
                        )}
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CitedMarks; 